/**
 * The partner's side of the executed agreement: give us your particulars, read it, sign it.
 *
 * Three states, and the component is only ever in one of them:
 *
 *   NEEDS PARTICULARS  We cannot name the parties yet. This page says so and points at Profile;
 *                      it does NOT collect anything. A partner's legal identity is written in
 *                      exactly one place, because the agreement and their invoices print the same
 *                      facts and a second form would eventually make the two disagree.
 *   READY TO SIGN      The agreement is issued. Read it in full, then tick and sign.
 *   SIGNED             Done. What was signed, when, and the fingerprint of the exact text.
 *
 * Accepting is a tick and a button — the same ceremony as the terms and the proposal, on purpose.
 * It is called ACCEPT, not sign, because that is what it is: there is no signature ceremony here
 * and a button labelled "Sign" would claim one. The stored status stays `signed` — an internal
 * state name, not a word anyone reads.
 * The rungs above it (a code to the signatory, then Aadhaar eSign) are worth climbing when a
 * partnership is worth litigating, not before. What makes this rung defensible is that the tick
 * is EXPLICIT, that it is attributed to a signatory the partner named in advance, and that what
 * gets stored is the hash of the exact text they were shown.
 */
import { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { renderMarkdown } from './TermsDocument';
import { FileSignature, ShieldCheck, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

// Mirrors agreements.ENTITY_TYPES on the server. Individual is first because a one-person
// partner is the normal case here, not an exception — the programme takes individuals at every
// level, and a form that buries "individual" under four company types says otherwise.
const ENTITY_TYPES = [
  { value: 'individual', label: 'Individual / sole proprietor' },
  { value: 'partnership', label: 'Partnership firm' },
  { value: 'llp', label: 'Limited liability partnership' },
  { value: 'private_limited', label: 'Private limited company' },
  { value: 'public_limited', label: 'Public limited company' },
  { value: 'trust_society', label: 'Trust or society' },
];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
  'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan',
  'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
];

const FIELD_LABEL = {
  entity_type: 'how you are constituted',
  legal_name: 'your registered legal name',
  registered_address: 'your registered address',
  gstin: 'your GSTIN',
  signatory_designation: 'your designation',
  pan_no: 'your PAN',
};

const formatDateTime = (str) =>
  str ? new Date(str).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '—';

/**
 * Your business and legal details — ONE form, rendered wherever they are needed.
 *
 * It appears on the agreement page (where you cannot sign without them) and on Profile (where
 * you go to change them). That is one form with two doors, not two forms: every field writes to
 * the same place, and `address`, `pan_no` and `gstin` are the partner's own record — the same
 * ones a payout reads. An earlier version asked for a "registered address" here while Profile
 * asked for an "address", which is two fields for one fact and a guaranteed disagreement.
 *
 * **The signatory defaults to YOU**, because for almost every partner it is. Only a company
 * signing through someone else has to name a second person, and until they say so the form does
 * not ask about one — "their email" on a form about your own business reads like a mistake,
 * because it was one.
 */
export const ParticularsForm = ({ initial, missing, user, onSaved, embedded = false }) => {
  const { getAuthHeaders, API_URL, refreshUser } = useAuth();
  const [form, setForm] = useState({
    entity_type: initial?.entity_type || '',
    legal_name: initial?.legal_name || user?.company_name || '',
    registered_address: initial?.registered_address || user?.address || '',
    signatory_designation: initial?.signatory_designation || '',
    pan_no: user?.pan_no || '',
    gstin: user?.gstin || '',
    place_of_supply: user?.place_of_supply || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e?.target ? e.target.value : e }));

  const isIndividual = form.entity_type === 'individual';
  // Someone signing for themselves holds no office, so asking for one is asking them to invent
  // an answer. A company signs THROUGH an office, and which one matters.
  const needsDesignation = form.entity_type && !isIndividual;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/partner/particulars`, form,
                                  { headers: getAuthHeaders() });
      if (res.data.missing?.length) {
        toast.error(`Still needed: ${res.data.missing.map((m) => FIELD_LABEL[m] || m).join(', ')}`);
      } else {
        toast.success('Saved.');
      }
      refreshUser?.();
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save that');
    } finally {
      setSaving(false);
    }
  };

  const body = (
    <form onSubmit={save} className="space-y-4">
      <div>
        <Label htmlFor="entity_type">How are you constituted?</Label>
        <Select value={form.entity_type} onValueChange={set('entity_type')}>
          <SelectTrigger id="entity_type" className="mt-1" data-testid="entity-type">
            <SelectValue placeholder="Choose one" />
          </SelectTrigger>
          <SelectContent>
            {ENTITY_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="legal_name">
          {isIndividual ? 'Your full legal name' : 'Registered legal name'}
        </Label>
        <Input id="legal_name" value={form.legal_name} onChange={set('legal_name')}
               className="mt-1" data-testid="legal-name"
               placeholder={isIndividual ? 'As it appears on your PAN' : 'As registered, including the suffix'} />
      </div>

      <div>
        <Label htmlFor="registered_address">
          {isIndividual ? 'Your address' : 'Registered address'}
        </Label>
        <Textarea id="registered_address" value={form.registered_address}
                  onChange={set('registered_address')} rows={2} className="mt-1"
                  data-testid="registered-address" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="pan_no">PAN</Label>
          <Input id="pan_no" value={form.pan_no} onChange={set('pan_no')}
                 className="mt-1 uppercase" placeholder="AAAAA1234A" maxLength={10}
                 data-testid="pan-no" />
          <p className="mt-1 text-xs text-[#64748B]">Required — we cannot pay you without it.</p>
        </div>
        <div>
          <Label htmlFor="gstin">GSTIN</Label>
          <Input id="gstin" value={form.gstin} onChange={set('gstin')}
                 className="mt-1 uppercase" placeholder="Leave blank if not registered"
                 maxLength={15} data-testid="gstin-particulars" />
          <p className="mt-1 text-xs text-[#64748B]">
            Optional. With one on file, GST is added to your commission.
          </p>
        </div>
        <div>
          <Label htmlFor="place_of_supply">Place of supply</Label>
          <Select value={form.place_of_supply} onValueChange={set('place_of_supply')}>
            <SelectTrigger id="place_of_supply" className="mt-1" data-testid="place-of-supply">
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {INDIAN_STATES.map((st) => (
                <SelectItem key={st} value={st}>{st}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="mt-1 text-xs text-[#64748B]">The state your GST is registered in.</p>
        </div>
      </div>

      {/* WHO ACCEPTS. Not a field, and not a choice.

          A partner used to be able to nominate someone else. The document named that person and
          then the ACCOUNT HOLDER clicked the button, so the record said someone who had never
          opened it had accepted it. That is an attribution, not an acceptance.

          Whoever is signed in accepts, full stop. Letting a colleague accept properly means
          letting them have their own login — planned, not built: docs/PLAN-team-members.md. */}
      <div className="rounded-lg border border-[#E2E2DF] bg-[#FAFAF8] p-4">
        <p className="text-sm text-[#1A1A1A]">
          <span className="font-medium">You accept the agreement</span>
          {user?.full_name ? ` — ${user.full_name} (${user.email}).` : '.'}
        </p>
        <p className="mt-1 text-xs text-[#64748B]">
          Whoever is signed in accepts it, and the record is theirs. We do not let one person
          accept in another&apos;s name.
        </p>

        {needsDesignation && (
          <div className="mt-4 sm:max-w-xs">
            <Label htmlFor="signatory_designation">Your designation</Label>
            <Input id="signatory_designation" value={form.signatory_designation}
                   onChange={set('signatory_designation')} className="mt-1"
                   placeholder="Director, Partner, Trustee…"
                   data-testid="signatory-designation" />
            <p className="mt-1 text-xs text-[#64748B]">
              The office the agreement is accepted under.
            </p>
          </div>
        )}
      </div>

      <Button type="submit" disabled={saving} data-testid="save-particulars">
        {saving ? 'Saving…' : 'Save these details'}
      </Button>
    </form>
  );

  if (embedded) return body;

  return (
    <Card data-testid="particulars-form">
      <CardHeader>
        <CardTitle className="text-base">Your business details, for the agreement</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-[#64748B] mb-5">
          A contract has to name who it is with. These are the same details your payouts use —
          changing them here changes them everywhere.
        </p>
        {body}
      </CardContent>
    </Card>
  );
};

/** Read it, tick it, sign it. */
const SignPanel = ({ agreement, onSigned }) => {
  const { getAuthHeaders, API_URL } = useAuth();
  const [document, setDocument] = useState(null);
  const [open, setOpen] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const readIt = async () => {
    setOpen((o) => !o);
    if (document) return;
    setLoadingDoc(true);
    try {
      const res = await axios.get(`${API_URL}/api/partner/agreement/document`,
                                  { headers: getAuthHeaders() });
      setDocument(res.data.document);
    } catch {
      toast.error('Could not load the agreement');
    } finally {
      setLoadingDoc(false);
    }
  };

  const sign = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/partner/agreement/sign`, { agreed: true },
                       { headers: getAuthHeaders() });
      toast.success('Accepted. Payouts and credit purchases are open.');
      onSigned?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not accept it');
    } finally {
      setBusy(false);
    }
  };

  const p = agreement.particulars || {};

  return (
    <Card className="border-[#1A1A1A]" data-testid="sign-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileSignature className="h-4 w-4" /> Your partnership agreement is ready to accept
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4 space-y-1 text-sm">
          <p className="text-[#1A1A1A]"><strong>{p.legal_name}</strong> · {p.entity_type_label}</p>
          <p className="text-[#64748B]">
            To be accepted by {p.signatory_name}{p.signatory_designation ? `, ${p.signatory_designation}` : ''}
          </p>
        </div>

        <Button variant="outline" size="sm" onClick={readIt} data-testid="read-agreement">
          {open ? <ChevronUp className="mr-1 h-3 w-3" /> : <ChevronDown className="mr-1 h-3 w-3" />}
          {open ? 'Hide the agreement' : 'Read the agreement'}
        </Button>

        {open && (
          <div className="mt-4 max-h-96 overflow-y-auto rounded-lg border border-[#E2E2DF] bg-[#FAFAF8] p-4"
               data-testid="agreement-text">
            {loadingDoc
              ? <Loader2 className="h-4 w-4 animate-spin text-[#64748B]" />
              : renderMarkdown(document)}
          </div>
        )}

        {/* An unticked box the partner ticks, deliberately — the same shape as registration.
            A single "Sign" button would make the signature a side effect of one click; the tick
            is what makes it an act, and the act is the part that is worth recording. */}
        <form onSubmit={sign} className="mt-5 border-t border-[#E2E2DF] pt-5">
          <div className="flex items-start gap-3 rounded-lg border border-[#E2E2DF] bg-[#FAFAF8] p-4">
            <Checkbox id="agree" checked={agreed} onCheckedChange={setAgreed}
                      className="mt-0.5" data-testid="agree-to-sign" />
            <Label htmlFor="agree"
                   className="cursor-pointer text-sm font-normal leading-relaxed text-[#64748B]">
              I have read the agreement above and I am authorised to accept it on behalf of{' '}
              <strong className="text-[#1A1A1A]">{p.legal_name}</strong>.
            </Label>
          </div>
          <Button type="submit" disabled={!agreed || busy} className="mt-4"
                  data-testid="confirm-sign">
            {busy ? 'Accepting…' : 'Accept the agreement'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

/** Signed. What, when, and the fingerprint of the exact text. */
const SignedPanel = ({ agreement }) => {
  const { getAuthHeaders, API_URL } = useAuth();
  const [document, setDocument] = useState(null);
  const [open, setOpen] = useState(false);

  const readIt = async () => {
    setOpen((o) => !o);
    if (document) return;
    try {
      const res = await axios.get(`${API_URL}/api/partner/agreement/document`,
                                  { headers: getAuthHeaders() });
      setDocument(res.data.document);
    } catch {
      toast.error('Could not load the agreement');
    }
  };

  const p = agreement.particulars || {};
  return (
    <Card data-testid="signed-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-4 w-4 text-green-600" /> Partnership agreement — accepted
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-[#1A1A1A]">
          Accepted by <strong>{agreement.signed_by_name}</strong> on{' '}
          {formatDateTime(agreement.signed_at)}, for <strong>{p.legal_name}</strong>.
        </p>
        <p className="mt-2 break-all text-xs text-[#64748B]">
          Rate schedule v{agreement.rate_card_version} · terms v{agreement.terms_version} ·
          document fingerprint {(agreement.document_hash || '').slice(0, 24)}…
        </p>
        <Button variant="outline" size="sm" className="mt-4" onClick={readIt}
                data-testid="read-signed-agreement">
          {open ? 'Hide it' : 'Read what you accepted'}
        </Button>
        {open && (
          <div className="mt-4 max-h-96 overflow-y-auto rounded-lg border border-[#E2E2DF] bg-[#FAFAF8] p-4">
            {document ? renderMarkdown(document)
                      : <Loader2 className="h-4 w-4 animate-spin text-[#64748B]" />}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Picks the one state that applies. `data` is the /api/partner/agreement payload.
 */
const AgreementSigner = ({ data, onChanged }) => {
  const { user } = useAuth();
  const agreement = data?.agreement;
  const missing = data?.missing_particulars || [];

  if (agreement?.status === 'signed') return <SignedPanel agreement={agreement} />;
  if (agreement?.status === 'issued') {
    return <SignPanel agreement={agreement} onSigned={onChanged} />;
  }
  if (missing.length) {
    return (
      <Card className="border-amber-200 bg-amber-50" data-testid="particulars-missing">
        <CardHeader>
          <CardTitle className="text-base">A few details first</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-amber-900">
            A contract has to name who it is with, and we are missing{' '}
            {missing.map((m) => FIELD_LABEL[m] || m).join(', ')}.
          </p>
          <p className="mt-2 text-sm text-amber-900/80">
            They live on your <strong>Profile</strong>, with the details your invoices and payouts
            use — the same facts, written once.
          </p>
          <Link to="/profile">
            <Button className="mt-4" data-testid="go-to-profile">Add them on Profile</Button>
          </Link>
        </CardContent>
      </Card>
    );
  }
  // Everything we need, nothing issued yet. Which of the two reasons it is matters: telling an
  // ALREADY-APPROVED partner that we are waiting on their approval is a dead end they cannot act
  // on and cannot query, and it is what this said before saving details started issuing the
  // agreement on the spot.
  const approved = data?.status === 'active';
  return (
    <Card>
      <CardContent className="py-5">
        <p className="text-sm text-[#64748B]">
          {approved
            ? <>Your details are with us and your partnership is approved — we are preparing your
                agreement now. Refresh in a moment, or tell us if it does not appear.</>
            : <>Your details are with us. We will issue your partnership agreement to accept once
                your application is approved — you will get an email when it is ready.</>}
        </p>
      </CardContent>
    </Card>
  );
};

export default AgreementSigner;
